import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Alert from 'flarum/common/components/Alert';
import app from 'flarum/forum/app';

declare global {
    interface Window {
        turnstile?: {
            render: (element: HTMLElement, config: any) => string;
            remove: (element: HTMLElement) => void;
        }
    }
}

interface IFormData {
    name: string;
    email: string;
    subject: string;
    message: string;
    turnstileToken: string | null;
}

export default class ContactForm extends Component {
    private formData: IFormData;
    private isLoading: boolean;
    private errors: string[];
    private success: boolean;

    oninit(vnode) {
        super.oninit(vnode);

        this.formData = {
            name: '',
            email: '',
            subject: '',
            message: '',
            turnstileToken: null
        };
        this.isLoading = false;
        this.errors = [];
        this.success = false;
    }

    view() {
        return m('.ContactForm', [
            this.errors.length > 0 && m('.Alert.Alert--error', [
                m('ul', this.errors.map(error => m('li', error)))
            ]),

            this.success && m('.Alert.Alert--success', 'Your message has been sent successfully!'),

            m('form', {
                onsubmit: (e: Event) => {
                    e.preventDefault();
                    this.submitForm();
                }
            }, [
                m('.Form-group', [
                    m('label', 'Name'),
                    m('input.FormControl', {
                        type: 'text',
                        value: this.formData.name,
                        placeholder: 'Your name',
                        required: true,
                        oninput: (e: InputEvent) => {
                            this.formData.name = (e.target as HTMLInputElement).value;
                        }
                    }),
                ]),

                m('.Form-group', [
                    m('label', 'Email'),
                    m('input.FormControl', {
                        type: 'email',
                        value: this.formData.email,
                        placeholder: 'your@email.com',
                        required: true,
                        oninput: (e: InputEvent) => {
                            this.formData.email = (e.target as HTMLInputElement).value;
                        }
                    }),
                ]),

                m('.Form-group', [
                    m('label', 'Subject'),
                    m('input.FormControl', {
                        type: 'text',
                        value: this.formData.subject,
                        placeholder: 'Message subject',
                        required: true,
                        oninput: (e: InputEvent) => {
                            this.formData.subject = (e.target as HTMLInputElement).value;
                        }
                    }),
                ]),

                m('.Form-group', [
                    m('label', 'Message'),
                    m('textarea.FormControl', {
                        value: this.formData.message,
                        placeholder: 'Your message here...',
                        rows: 5,
                        required: true,
                        oninput: (e: InputEvent) => {
                            this.formData.message = (e.target as HTMLInputElement).value;
                        }
                    }),
                ]),

                m('.Form-group', [
                    m('.Blazite-Turnstile.Form-group', {
                        oncreate: (vnode) => {
                            if (window.turnstile) {
                                window.turnstile.render(vnode.dom, {
                                    sitekey: app.forum.attribute('blazite-turnstile.site_key'),
                                    theme: this.getCurrentTheme(),
                                    callback: (token) => {
                                        this.formData.turnstileToken = token;
                                        m.redraw();
                                    },
                                    'error-callback': () => {
                                        this.formData.turnstileToken = null;
                                        this.errors.push('Turnstile verification failed. Please try again.');
                                        m.redraw();
                                    },
                                    'expired-callback': () => {
                                        this.formData.turnstileToken = null;
                                        this.errors.push('Turnstile verification expired. Please try again.');
                                        m.redraw();
                                    }
                                });
                            }
                        },
                        onremove: (vnode) => {
                            if (window.turnstile) {
                                window.turnstile.remove(vnode.dom);
                            }
                        }
                    })
                ]),

                m('.Form-group', [
                    Button.component({
                        type: 'submit',
                        className: 'Button Button--primary',
                        loading: this.isLoading,
                        disabled: this.isLoading || !this.formData.turnstileToken,
                    }, 'Send Message')
                ]),
            ])
        ]);
    }

    getCurrentTheme() {
        const getTheme = flarum.extensions['fof-nightmode']?.getTheme;
        const Themes = flarum.extensions['fof-nightmode']?.Themes;

        if (getTheme && Themes) {
            let currentTheme = getTheme();

            if (currentTheme === Themes.AUTO) {
                currentTheme = window.matchMedia('(prefers-color-scheme:dark)').matches ? Themes.DARK : Themes.LIGHT;
            }

            if (currentTheme === Themes.DARK) {
                return 'dark';
            } else if (currentTheme === Themes.LIGHT) {
                return 'light';
            }
        }

        return !!!app.forum.attribute('turnstile_dark_mode') ? 'light' : 'dark';
    }

    private validateForm(): boolean {
        this.errors = [];

        if (!this.formData.turnstileToken) {
            this.errors.push('Please complete the Turnstile challenge');
        }

        if (!this.formData.name.trim()) {
            this.errors.push('Name is required');
        }

        if (!this.formData.email.trim()) {
            this.errors.push('Email is required');
        } else if (!this.isValidEmail(this.formData.email)) {
            this.errors.push('Please enter a valid email address');
        }

        if (!this.formData.subject.trim()) {
            this.errors.push('Subject is required');
        }

        if (!this.formData.message.trim()) {
            this.errors.push('Message is required');
        }

        return this.errors.length === 0;
    }

    private isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    private async submitForm() {
        if (!this.validateForm()) {
            return;
        }

        this.isLoading = true;
        this.errors = [];
        m.redraw();

        try {
            await app.request({
                method: 'POST',
                url: `${app.forum.attribute('apiUrl')}/contact`,
                body: {
                    data: {
                        ...this.formData,
                        'cf-turnstile-response': this.formData.turnstileToken
                    }
                }
            });

            this.success = true;
            this.formData = {
                name: '',
                email: '',
                subject: '',
                message: '',
                turnstileToken: null
            };

            window.scrollTo(0, 0);
        } catch (error) {
            if (error.response?.errors) {
                this.errors = error.response.errors.map(e => e.detail || 'An error occurred');
            } else {
                this.errors.push('An error occurred while sending your message. Please try again.');
            }
            window.scrollTo(0, 0);
        } finally {
            this.isLoading = false;
            m.redraw();
        }
    }
}
